import * as React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { addDays } from 'date-fns';
import { Button } from '~/components/ui/button';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/primitives';
import { EmptyState, LoadingState, PageHeader } from '~/components/shared/states';
import { api } from '~/lib/api';
import { queryKeys } from '~/lib/queries';
import { formatRelativeDay, formatSlotRange, fullName } from '~/lib/utils';
import { type SlotWithIntervention, type TechnicianSummary } from '@hch/shared';

export function AdminPlanningPage() {
  const [technicianId, setTechnicianId] = React.useState<string>('');
  const [dayOffset, setDayOffset] = React.useState(0);

  const day = addDays(new Date(), dayOffset);
  const from = new Date(day.setHours(0, 0, 0, 0)).toISOString();
  const to = new Date(day.setHours(23, 59, 59, 999)).toISOString();

  const { data: technicians } = useQuery({
    queryKey: queryKeys.technicians,
    queryFn: () => api.get<TechnicianSummary[]>('/technicians'),
  });

  const { data: slots, isLoading } = useQuery({
    queryKey: queryKeys.slots({ from, to, technicianId }),
    queryFn: () =>
      api.get<SlotWithIntervention[]>('/slots', {
        from,
        to,
        technicianId: technicianId || undefined,
      }),
  });

  // Regroupement par technicien : un planning se lit par colonne, pas en liste plate.
  const byTechnician = React.useMemo(() => {
    const map = new Map<
      string,
      { technician: SlotWithIntervention['technician']; slots: SlotWithIntervention[] }
    >();
    for (const slot of slots ?? []) {
      const entry = map.get(slot.technician.id);
      if (entry) entry.slots.push(slot);
      else map.set(slot.technician.id, { technician: slot.technician, slots: [slot] });
    }
    return [...map.values()];
  }, [slots]);

  return (
    <div className="app-page">
      <PageHeader
        title="Planning"
        description="Occupation des creneaux, technicien par technicien."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setDayOffset((v) => v - 1)}>
              Jour precedent
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDayOffset(0)}>
              Aujourd'hui
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDayOffset((v) => v + 1)}>
              Jour suivant
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <p className="font-medium capitalize">
          {formatRelativeDay(addDays(new Date(), dayOffset))}
        </p>
        <Select
          value={technicianId || 'all'}
          onValueChange={(v) => setTechnicianId(v === 'all' ? '' : v)}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Tous les techniciens" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les techniciens</SelectItem>
            {technicians?.map((technician) => (
              <SelectItem key={technician.id} value={technician.id}>
                {fullName(technician)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <LoadingState /> : null}

      {!isLoading && byTechnician.length === 0 ? (
        <EmptyState
          title="Aucun creneau ce jour"
          description="Generez des creneaux depuis les modeles de planification."
          action={
            <Button asChild variant="outline">
              <Link to="/admin/modeles">Voir les modeles</Link>
            </Button>
          }
        />
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {byTechnician.map(({ technician, slots: technicianSlots }) => (
          <Card key={technician.id}>
            <CardHeader>
              <CardTitle className="text-sm">{fullName(technician)}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-1">
              {technicianSlots.map((slot) => (
                <div
                  key={slot.id}
                  className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm"
                >
                  <span className="tabular-nums text-muted-foreground">
                    {formatSlotRange(slot.startsAt, slot.endsAt)}
                  </span>
                  <span className="flex-1 truncate">
                    {slot.intervention ? slot.intervention.packageName : null}
                  </span>
                  <Badge
                    variant={
                      slot.status === 'BOOKED'
                        ? 'default'
                        : slot.status === 'BLOCKED'
                          ? 'destructive'
                          : 'outline'
                    }
                  >
                    {slot.status === 'BOOKED'
                      ? 'Reserve'
                      : slot.status === 'BLOCKED'
                        ? 'Bloque'
                        : 'Libre'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Modeles de planification
